import { useEffect } from "react";
import { Link } from "react-router-dom";
import { NavHeader } from "../../components/NavHeader";
import { Footer } from "../../components/Footer";

export const LearnArticleLayout = ({
  slug,
  title,
  subtitle,
  seo_title,
  seo_desc,
  read_time,
  updated,
  children,
  related = [],
}) => {
  useEffect(() => {
    document.title = `${seo_title} · Astrarium`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", seo_desc);
    window.scrollTo(0, 0);
  }, [seo_title, seo_desc]);

  return (
    <>
      <NavHeader />
      <main
        data-testid={`learn-article-${slug}`}
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
      >
        <nav aria-label="Breadcrumb" className="mb-8 text-[11px] uppercase tracking-[0.25em] text-slate-500">
          <Link to="/learn" className="hover:text-stardust transition-colors" data-testid="learn-breadcrumb-hub">
            The learn library
          </Link>
          <span className="mx-3 text-stardust/40">/</span>
          <span className="text-stardust/80">Essay</span>
        </nav>

        <article className="mb-16">
          <header className="mb-10">
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px] mb-4">{read_time}{updated ? ` · updated ${updated}` : ""}</p>
            <h1 className="font-display text-4xl sm:text-6xl text-[#F5EBD8] leading-[1.05] mb-4">
              {title}<span className="text-stardust">.</span>
            </h1>
            <p className="font-serif-italic text-slate-300/90 text-lg sm:text-2xl leading-relaxed">
              {subtitle}
            </p>
          </header>

          <div className="prose-astrarium text-slate-200 leading-[1.8] text-base sm:text-lg">
            {children}
          </div>
        </article>

        {related.length > 0 && (
          <section className="border-t border-white/[0.06] pt-10 mb-12">
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px] mb-5">Read next</p>
            <div className="space-y-3">
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  data-testid={`learn-related-${r.slug}`}
                  className="block p-4 sm:p-5 rounded-lg bg-white/[0.02] border border-white/10 hover:border-stardust/40 hover:bg-white/[0.04] transition-all group"
                >
                  <p className="font-display text-lg sm:text-xl text-[#F5EBD8] group-hover:text-stardust transition-colors leading-tight">{r.label}</p>
                  <p className="text-slate-400 text-sm mt-1">{r.hint}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="text-center py-10 border-t border-white/[0.06]">
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-4 leading-tight">
            Draw your own almanac.
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto mb-6">
            Astrarium takes the ideas in this essay and reads them onto your specific chart. Free, no sign-up, seven chapters, one clear reading.
          </p>
          <Link
            to="/"
            data-testid={`learn-article-cta-${slug}`}
            className="inline-flex items-center bg-stardust hover:bg-stardust-light text-cosmic-900 rounded-md uppercase tracking-[0.3em] text-xs px-10 py-5 font-medium shadow-[0_0_28px_rgba(226,194,117,0.25)] hover:shadow-[0_0_44px_rgba(226,194,117,0.45)] transition-all"
          >
            Get my reading ✦
          </Link>
        </section>
      </main>
      <Footer />
    </>
  );
};

// Helper components for the essay body

export const H2 = ({ children }) => (
  <h2 className="font-display text-2xl sm:text-3xl text-[#F5EBD8] mt-10 mb-4 leading-tight">{children}</h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-display text-xl sm:text-2xl text-stardust/90 mt-8 mb-3 leading-tight">{children}</h3>
);

export const P = ({ children }) => (
  <p className="mb-5 text-slate-300 leading-[1.8]">{children}</p>
);

export const UL = ({ children }) => (
  <ul className="mb-6 space-y-2 pl-1">{children}</ul>
);

export const LI = ({ children }) => (
  <li className="flex items-start gap-3 text-slate-300 leading-relaxed">
    <span className="text-stardust mt-1.5 shrink-0 text-sm">✦</span>
    <span>{children}</span>
  </li>
);

export const Quote = ({ children }) => (
  <blockquote className="my-8 pl-5 border-l-2 border-stardust/50 font-serif-italic text-stardust/90 text-lg leading-relaxed">
    {children}
  </blockquote>
);
